import { getApiUrl } from '@/lib/api-config';
import { dispatchAccessRevoked } from '@/services/accessRevoked';
import { fetchWithRateLimitRetry } from '@/services/rateLimitedFetch';

export const getApiBaseUrl = (): string => {
  let baseUrl: string;
  const configuredUrl = getApiUrl();
  if (configuredUrl) {
    baseUrl = configuredUrl;
  } else {
    baseUrl = import.meta.env.VITE_LIVE_API_BASE_URL ?? 'http://localhost:5000/api';
  }

  const stripped = baseUrl.replace(/\/+$/, '');
  if (!stripped.endsWith('/api')) {
    return `${stripped}/api`;
  }
  return stripped;
};

export class ApiError extends Error {
  public status: number;
  public statusText: string;
  public data?: Record<string, unknown>;

  constructor(status: number, statusText: string, message: string, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    
    let errorData: Record<string, unknown> | undefined;
    try {
      errorData = await response.json();
      errorMessage = (errorData?.message as string) || (errorData?.title as string) || errorMessage;
    } catch {
    }

    if (response.status === 403 && errorData?.error === 'AccessRevoked') {
      dispatchAccessRevoked();
    }
    
    throw new ApiError(response.status, response.statusText, errorMessage, errorData);
  }
  
  // A successful response may legitimately carry no body (e.g. 204 No Content,
  // or a 200 with Content-Length: 0). Calling response.json() on an empty body
  // throws, so treat any bodyless success as an empty result rather than an error.
  if (response.status === 204 || response.status === 205 || hasNoResponseBody(response)) {
    return {} as T;
  }

  try {
    return await response.json();
  } catch {
    // Reaching here means the server returned a success status but the body was
    // empty or not valid JSON. An empty body is a valid "no content" result, so
    // resolve with an empty object instead of surfacing a false failure.
    return {} as T;
  }
}

/** True when a successful response has no payload to parse (empty/zero-length body). */
function hasNoResponseBody(response: Response): boolean {
  const contentLength = response.headers?.get?.('content-length');
  return contentLength === '0';
}

/**
 * GET request de-duplication and short-lived caching.
 *
 * Telemetry showed the SPA issuing the same GET many times within a single user
 * operation (e.g. GetOrganizationCurrent up to 57x, Projects/GetByIds 435x),
 * each a self-referential HTTP call over the public API URL.
 *
 * - In-flight de-duplication (always on): concurrent identical GETs share one
 *   network request. This has no staleness risk — every waiter resolves with the
 *   same fresh response.
 * - Short-lived cache (opt-in via `cacheTtlMs`): a burst of sequential identical
 *   GETs within the TTL window reuses the last response. Kept opt-in so callers
 *   only enable it for data that tolerates a brief cache.
 *
 * Requests that pass an AbortSignal bypass both paths to preserve per-caller
 * cancellation semantics.
 */
const inFlightGets = new Map<string, Promise<unknown>>();
const getCache = new Map<string, { expiry: number; value: unknown }>();

function cloneResult<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Clears cached and in-flight GET state. Exposed for tests and post-mutation invalidation. */
function invalidateGetCache(): void {
  inFlightGets.clear();
  getCache.clear();
}

export const apiClient = {
  invalidateGetCache,

  async get<T>(
    endpoint: string,
    options?: { signal?: AbortSignal; cacheTtlMs?: number }
  ): Promise<T> {
    const url = `${getApiBaseUrl()}${endpoint}`;

    const doFetch = async (): Promise<T> => {
      const response = await fetchWithRateLimitRetry(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }, options?.signal);
      return await handleResponse<T>(response);
    };

    // Cancellable requests bypass de-duplication/caching so one caller's abort
    // cannot affect another's request.
    if (options?.signal) {
      return doFetch();
    }

    const ttl = options?.cacheTtlMs ?? 0;

    if (ttl > 0) {
      const cached = getCache.get(url);
      if (cached && cached.expiry > Date.now()) {
        return cloneResult(cached.value) as T;
      }
    }

    const existing = inFlightGets.get(url);
    if (existing) {
      return cloneResult(await (existing as Promise<T>));
    }

    const request = doFetch();
    inFlightGets.set(url, request);

    try {
      const value = await request;
      if (ttl > 0) {
        getCache.set(url, { expiry: Date.now() + ttl, value });
      }
      return cloneResult(value);
    } finally {
      inFlightGets.delete(url);
    }
  },

  async post<T, D = unknown>(endpoint: string, data: D, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await fetchWithRateLimitRetry(`${getApiBaseUrl()}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }, options?.signal);
    return await handleResponse<T>(response);
  },

  async put<T, D = unknown>(endpoint: string, data: D, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await fetchWithRateLimitRetry(`${getApiBaseUrl()}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }, options?.signal);
    return await handleResponse<T>(response);
  },

  async patch<T, D = unknown>(endpoint: string, data?: D, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await fetchWithRateLimitRetry(`${getApiBaseUrl()}${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }, options?.signal);
    return await handleResponse<T>(response);
  },

  async delete<T>(endpoint: string, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await fetchWithRateLimitRetry(`${getApiBaseUrl()}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    }, options?.signal);
    return await handleResponse<T>(response);
  },
};
